import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, Apple, Monitor, ChevronDown } from "lucide-react";

const COMMANDS = [
  "Open Slack",
  "Draft email to the team",
  "Open Gmail",
  "Open Microsoft Teams",
  "Take a note",
  "Search for meeting notes",
];

const MAC_DOWNLOAD = "https://www.dropbox.com/scl/fi/zfx37co1gqhwzgcvx62n3/ClawdVoice-0.0.1.dmg?rlkey=qu5iuczq45oe3qwr50xzqdump&st=k4rgb2p7&dl=1";
const WIN_DOWNLOAD = "https://www.dropbox.com/scl/fi/cwk1759sxp65wox9bxii6/ClawdVoice-0.0.1.exe?rlkey=a060oado897b4hepk9t096s1h&st=ko4gdltx&dl=1";

export default function Hero() {
  const [isPressed, setIsPressed] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [commandIndex, setCommandIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [charIndex, setCharIndex] = useState(0);

  // Typing effect when listening
  useEffect(() => {
    if (!isListening) {
      setDisplayText("");
      setCharIndex(0);
      return;
    }
    const cmd = COMMANDS[commandIndex];
    if (charIndex < cmd.length) {
      const t = setTimeout(() => {
        setDisplayText(cmd.slice(0, charIndex + 1));
        setCharIndex((c) => c + 1);
      }, 60);
      return () => clearTimeout(t);
    } else {
      // After finishing, cycle to next command after pause
      const t = setTimeout(() => {
        setIsListening(false);
        setCommandIndex((i) => (i + 1) % COMMANDS.length);
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [isListening, charIndex, commandIndex]);

  const handleFnPress = () => {
    setIsPressed(true);
    setIsListening(true);
    setDisplayText("");
    setCharIndex(0);
  };

  const handleFnRelease = () => {
    setIsPressed(false);
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-20 bg-[#FAFAFA] overflow-hidden"
    >
      {/* Subtle grid lines background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#E4E4E7 1px, transparent 1px), linear-gradient(90deg, #E4E4E7 1px, transparent 1px)",
          backgroundSize: "80px 80px",
          opacity: 0.3,
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left: Copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 text-zinc-500 text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Now available for Mac &amp; Windows
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
              style={{ fontFamily: "Manrope, sans-serif" }}
              className="text-5xl md:text-7xl font-bold tracking-tighter leading-[1.05] text-zinc-900 mb-6"
            >
              Your voice.
              <br />
              Your computer.
              <br />
              <span className="text-orange-500">Instant.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg text-zinc-500 leading-relaxed mb-10 max-w-lg"
            >
              Hold{" "}
              <kbd className="font-mono text-sm bg-zinc-900 text-white px-2 py-0.5 rounded-sm">
                FN
              </kbd>{" "}
              and speak. ClawdVoice opens apps, drafts emails, and types for
              you — no clicks, no interruptions.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              id="download"
              className="flex flex-col sm:flex-row gap-3 flex-wrap"
            >
              <a
                data-testid="download-mac-btn"
                href={MAC_DOWNLOAD}
                className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-4 rounded-md shadow-md hover:shadow-lg transition-all active:scale-95"
              >
                <Apple size={22} />
                Download for Mac
              </a>
              <a
                data-testid="download-windows-btn"
                href={WIN_DOWNLOAD}
                className="inline-flex items-center gap-3 bg-zinc-900 hover:bg-zinc-700 text-white font-semibold text-lg px-8 py-4 rounded-md transition-all active:scale-95"
              >
                <Monitor size={22} />
                Download for Windows
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.38 }}
              className="flex items-center gap-3 mt-1"
            >
              <a
                data-testid="calendly-cta-btn"
                href="https://calendly.com/angaddubey10/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-zinc-500 hover:text-zinc-800 text-sm font-medium transition-colors"
              >
                <Calendar size={15} />
                Meet with Founders
              </a>
            </motion.div>
          </div>

          {/* Right: Interactive FN Key demo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col items-center justify-center gap-10"
          >
            {/* Big FN Key */}
            <div className="flex flex-col items-center gap-6">
              <p className="text-xs text-zinc-400 font-mono uppercase tracking-widest">
                {isListening ? "Listening..." : "Hold to speak"}
              </p>

              <div
                data-testid="fn-key-hero"
                className={`fn-key text-5xl font-bold w-40 h-40 select-none ${isPressed ? "pressed" : ""}`}
                onMouseDown={handleFnPress}
                onMouseUp={handleFnRelease}
                onMouseLeave={handleFnRelease}
                onTouchStart={handleFnPress}
                onTouchEnd={handleFnRelease}
              >
                FN
              </div>

              {/* Waveform */}
              <div
                className={`flex items-center gap-1 h-10 transition-opacity duration-200 ${
                  isListening ? "opacity-100" : "opacity-0"
                }`}
              >
                {Array.from({ length: 7 }).map((_, i) => (
                  <div
                    key={i}
                    className="waveform-bar w-1.5 bg-orange-500 rounded-full"
                    style={{ height: `${20 + Math.random() * 20}px` }}
                  />
                ))}
              </div>
            </div>

            {/* Command output box */}
            <div
              data-testid="command-display"
              className="w-full max-w-sm bg-white border border-zinc-200 rounded-md p-5 shadow-sm min-h-[80px] flex items-center"
            >
              {displayText ? (
                <span className="font-mono text-zinc-800 text-base">
                  {displayText}
                  <span className="typing-cursor text-orange-500 ml-0.5">|</span>
                </span>
              ) : (
                <span className="text-zinc-300 text-sm font-mono">
                  Press FN + speak a command...
                </span>
              )}
            </div>

            <p className="text-xs text-zinc-400 text-center max-w-xs leading-relaxed">
              Try pressing the{" "}
              <kbd className="font-mono text-xs bg-zinc-100 border border-zinc-200 px-1.5 py-0.5 rounded-sm text-zinc-600">
                FN
              </kbd>{" "}
              key above to see ClawdVoice in action
            </p>
          </motion.div>
        </div>

        {/* Scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-zinc-400"
        >
          <span className="text-xs font-mono uppercase tracking-widest">
            Scroll
          </span>
          <ChevronDown size={16} className="animate-bounce" />
        </motion.div>
      </div>
    </section>
  );
}
